import { useEffect, type ReactNode } from 'react';
import {
  canAccessView,
  canAccessViewFull,
  getAppViewLabel,
  getAppViewMeta,
  type AppView,
} from '@aegislab/shared';
import { getViewState } from '@/shared/config';
import { WindowSkeleton } from '@/shared/ui/atoms';
import { DataLoadError } from '@/shared/ui/DataLoadError';
import { useWindowStore, useAuthStore } from '@/shared/model';
import { solAnchor } from '@/shared/lib/format';
import { usePageContext } from '@/features/page-context';
import {
  TodaySealedTitle,
  TodaySealedFooter,
  useTodayCloseStore,
  useTodayHistoryStore,
} from '@/features/today';
import { useWorkLogCount, useWorkLogLifetimeStats } from '@/features/worklog';
import { TeaserWindow } from '@/shared/ui/templates';
import * as s from '@/pages/MainPage/ui/MainPage.css';

/**
 * 캐퍼빌리티 미확정 화면 — ViewGate(공개 여부)·AccessGuard(접근 권한) 공용.
 *
 * 두 게이트는 묻는 질문이 다르지만 **믿을 수 없는 caps 를 만났을 때의 답은 같다**: 대기는
 * 대기로, 실패는 재시도 가능한 실패로 보여준다. 문구 골격("… 권한 확인 중" / "권한 정보를
 * 불러오지 못했습니다")과 재시도 동작이 갈라지면 같은 원인의 화면이 두 가지로 보이므로
 * 여기 한 곳에 둔다. 호출부마다 다른 건 실패 이유 한 줄뿐이라 그것만 받는다.
 *
 * 로딩을 실패보다 먼저 본다 — 재조회 중(이전 실패가 남아 있는) 순간을 실패로 굳히지 않는다.
 * 확정 판정과 fail-closed(본문 미마운트)는 호출부 몫 — 여기는 미확정일 때만 렌더된다.
 *
 * 상태·훅이 없어 컴포넌트가 아니라 렌더 헬퍼로 둔다(호출부에 그대로 인라인된다).
 */
function capabilityTrustFallback({
  viewLabel,
  failureDescription,
  isLoading,
  onRetry,
}: {
  viewLabel: string;
  failureDescription: string;
  isLoading: boolean;
  onRetry: () => void;
}) {
  if (isLoading) {
    return <WindowSkeleton label={`${viewLabel} 권한 확인 중`} />;
  }
  return <DataLoadError subject="권한 정보" description={failureDescription} onRetry={onRetry} />;
}

/**
 * 피처 플래그 게이트 — getViewState 기반 분기 + 캐퍼빌리티 신뢰도 축.
 * - `live`: children 그대로 렌더. 로딩·실패와 무관하다 — 캐퍼빌리티 없이도 live 인 GA 뷰가
 *   권한 조회에 막히면 안 된다.
 * - 강등 후보(`teaser`/`hidden`): 판정 전에 캐퍼빌리티를 믿을 수 있는지 먼저 본다. 조회가
 *   실패하면 teamCapabilities 가 [] 로 붕괴하는데 그 [] 는 "베타 권한 없음"과 구분되지 않아,
 *   그대로 소비하면 베타 공개 중인 창이 '출시 예정'으로 강등된다. 로딩 중의 [] 도 같은 함정.
 *   - isLoading: 대기 표시 (확정 전엔 갈라지지 않는다 — 깜빡 강등 방지)
 *   - isError: 실패 표시 + 재시도 (실패를 미출시로 오독하지 않는다)
 *   - 확정: TeaserWindow. teaser는 BETA_TESTER가 우회해 live가 됨, hidden은 우회 없음.
 */
export function ViewGate({
  viewId,
  viewLabel,
  children,
  glitchContent,
}: {
  viewId: string;
  viewLabel: string;
  children: ReactNode;
  glitchContent?: ReactNode;
}) {
  const { teamCapabilities, isLoading, isError, refetchTeams } = usePageContext();
  const state = getViewState(viewId, teamCapabilities);
  if (state === 'live') {
    return children;
  }
  if (isLoading || isError) {
    return capabilityTrustFallback({
      viewLabel,
      failureDescription: `${viewLabel} 공개 여부를 확인할 수 없습니다.`,
      isLoading,
      onRetry: refetchTeams,
    });
  }
  return <TeaserWindow viewId={viewId} viewLabel={viewLabel} glitchContent={glitchContent} />;
}

function AccessDeniedPlaceholder({ view }: { view: AppView }) {
  const title = getAppViewLabel(view);
  return (
    <div className={s.comingSoonWrapper}>
      <span className={s.comingSoonIcon}>🔒</span>
      <h2 className={s.comingSoonTitle}>{title} — 접근 권한 없음</h2>
      <p className={s.comingSoonDesc}>이 페이지에 접근할 권한이 없습니다.</p>
    </div>
  );
}

/**
 * 접근 가드 — 레벨(+ `full` 이면 캐퍼빌리티) 판정 + 캐퍼빌리티 신뢰도 축.
 * - 허용이면 즉시 children. 지금 caps 로도 이미 열리므로 로딩·실패를 물을 이유가 없다.
 * - 거부이고 `full` 일 때만 신뢰도를 따진다. 조회가 실패하면 teamCapabilities 가 [] 로 붕괴하는데
 *   그 [] 는 "권한 없음"과 구분되지 않아, 그대로 소비하면 **재시도 가능한 실패가 영구 거부처럼
 *   보인다**. 로딩 중의 [] 도 같은 함정(깜빡 거부).
 *   - isLoading: 대기 표시 (확정 전엔 갈라지지 않는다)
 *   - isError: 실패 표시 + 재시도 (실패를 권한 부족으로 오독하지 않는다)
 *   fail-closed 는 유지 — 확정 전에 본문을 열지는 않는다.
 * - non-full(레벨 전용) 거부는 캐퍼빌리티와 무관하므로 그대로 거부한다. 여기에 실패 축을
 *   끌어들이면 고쳐지지 않을 재시도를 권하게 된다.
 */
export function AccessGuard({
  view,
  full,
  children,
}: {
  view: AppView;
  full?: boolean;
  children: ReactNode;
}) {
  const {
    user,
    teamCapabilities,
    isLoading,
    isError,
    refetchTeams,
    viewEligibilities,
    isEligibilityLoading,
    isEligibilityError,
    refetchEligibility,
  } = usePageContext();
  // roles 기본값은 ['PLAYER'] — 미확정 사용자에게 역할 요건이 걸린 창(대표 콘솔)을
  // 열어 주지 않는다. 아래 canPerform 호출부들과 같은 폴백이다.
  const roles = user?.roles ?? ['PLAYER'];
  const hasAccess = full
    ? user?.level &&
      canAccessViewFull(user.level, teamCapabilities, view, roles, user.teamRole, viewEligibilities)
    : user?.level &&
      canAccessView(user.level, view, roles, {
        teamRole: user.teamRole,
        capabilities: teamCapabilities,
        eligibilities: viewEligibilities,
      });
  if (hasAccess) {
    return children;
  }
  // 서버 판정 자격(명단 내려받기)이 걸린 창 — 자격 조회의 확인 중·실패를 "자격 없음"과 가른다
  // (docs/design/ia/creator-lists-ia.md §1 표). 자격 없음이 확정되면 아래 공통 막힌 화면이다.
  if (getAppViewMeta(view).requiresEligibility && (isEligibilityLoading || isEligibilityError)) {
    const viewLabel = getAppViewLabel(view);
    return capabilityTrustFallback({
      viewLabel,
      failureDescription: `${viewLabel} 자격을 확인할 수 없습니다.`,
      isLoading: isEligibilityLoading,
      onRetry: refetchEligibility,
    });
  }
  if (full && (isLoading || isError)) {
    const viewLabel = getAppViewLabel(view);
    return capabilityTrustFallback({
      viewLabel,
      failureDescription: `${viewLabel} 접근 권한을 확인할 수 없습니다.`,
      isLoading,
      onRetry: refetchTeams,
    });
  }
  return <AccessDeniedPlaceholder view={view} />;
}

// Daily Sealed Log motif title/footer 의 실 데이터 연결.
// entryNo = 본인 workLogs 누적, sol = 전환일(있으면)·입사일 기준 경과일 + 그 기준 라벨,
// lifetime = SUBMITTED 일지의 (1 + revisionCount) 합 (재마감 포함 마감 횟수).
export function TodaySealedTitleConnected() {
  const userId = useAuthStore((st) => st.user?.id);
  const hireDate = useAuthStore((st) => st.user?.hireDate);
  const internConvertedAt = useAuthStore((st) => st.user?.internConvertedAt);
  const { data: count } = useWorkLogCount(userId);
  const entryNo = count?.count ?? 0;
  // SOL은 shared util(KST 기준) — 기준(입사·정규직)을 라벨과 한 벌로 받는다.
  // null(둘 다 미설정·invalid)이면 sol 미생성 → 표기 생략.
  const sol = solAnchor(hireDate, internConvertedAt) ?? undefined;
  return <TodaySealedTitle entryNo={entryNo} sol={sol} />;
}

export function TodaySealedFooterConnected() {
  const userId = useAuthStore((st) => st.user?.id);
  const { data: lifetimeStats } = useWorkLogLifetimeStats(userId);
  // submittedCount + totalRevisions = filter(SUBMITTED).reduce((s,wl)=>s+1+wl.revisionCount,0) 와 동치.
  const lifetime = (lifetimeStats?.submittedCount ?? 0) + (lifetimeStats?.totalRevisions ?? 0);
  // H-2 — 마감 CTA를 band 우측에 승격. 마감 로직은 TodayMemo 소유, requestOpen은 열기 신호만.
  const busy = useTodayCloseStore((s) => s.busy);
  const submitted = useTodayCloseStore((s) => s.submitted);
  const canClose = useTodayCloseStore((s) => s.canClose);
  const requestOpen = useTodayCloseStore((s) => s.requestOpen);
  return (
    <TodaySealedFooter
      lifetime={lifetime}
      onOpenArchive={() => useTodayHistoryStore.getState().requestOpen()}
      sealAction={canClose ? { busy, submitted, onClose: requestOpen } : undefined}
    />
  );
}

export function WorkLogRedirectWindow() {
  useEffect(() => {
    const store = useWindowStore.getState();
    const sourceContext = store.navigationContext;
    if (sourceContext?.targetWindowId === 'work-log') {
      const { targetWindowId: _targetWindowId, ...context } = sourceContext;
      store.openWindow('today', context);
    } else {
      store.openWindow('today');
    }
    store.closeWindow('work-log');
  }, []);

  return (
    <div className={s.comingSoonWrapper}>
      <span className={s.comingSoonIcon}>↪</span>
      <h2 className={s.comingSoonTitle}>오늘 일지로 이동 중</h2>
      <p className={s.comingSoonDesc}>
        업무 일지는 오늘 일지 창 하나로 통합해 중복 마운트를 막습니다.
      </p>
    </div>
  );
}

export function AdminAttendanceWindow({ children }: { children: ReactNode }) {
  const { user, teamCapabilities, isLoading, isError, refetchTeams, viewEligibilities } =
    usePageContext();
  const allowed = user?.level
    ? canAccessView(user.level, 'admin-attendance', user.roles ?? ['PLAYER'], {
        teamRole: user.teamRole,
        capabilities: teamCapabilities,
        eligibilities: viewEligibilities,
      })
    : false;
  if (allowed) {
    // 정본 통일(redesign §10, 2026-05-09): 6-tab(AdminAttendancePage) deprecate → 단일 bento(v2).
    // 기존 nav/딥링크/저장탭이 가리키는 admin-attendance 윈도우가 그대로 v2 대시보드를 렌더한다.
    // LEADER 팀 스코핑은 v2의 useAggregateAttendances=BE 권위적 scope(#865)가 대체 — FE teamId 불필요.
    return children;
  }
  // attendance:manage 는 HR_MANAGEMENT capability 로도 오므로 팀 조회가 끝나기 전의 [] 와 조회 실패를
  // 거부로 읽지 않는다 — AccessGuard(full) 와 같은 신뢰도 축. 확정 전에 본문을 열지는 않는다.
  if (isLoading || isError) {
    return capabilityTrustFallback({
      viewLabel: getAppViewLabel('admin-attendance'),
      failureDescription: '근태 운영 접근 권한을 확인할 수 없습니다.',
      isLoading,
      onRetry: refetchTeams,
    });
  }
  return <AccessDeniedPlaceholder view="admin-attendance" />;
}
